<template>
  <div id="filter-token-component" class="filter-token relative inline-block mb-8 mb-24 mt-8">
    <button
      :id="modalLabel + '-filter-token-button'"
      type="button"
      :class="`filter-token-button` + selectedClass + ' ' + buttonClasses"
      :aria-expanded="buttonOnly ? undefined : buttonToggled"
      :aria-haspopup="buttonOnly ? undefined : 'dialog'"
      @click.prevent="buttonHandler"
    >
      <slot name="buttonPrefix" />
      {{ buttonLabel }}
      <CaretIcon v-if="!buttonOnly" class="ml-6" :class="iconDirection" color="currentColor" aria-hidden="true" />
    </button>
    <div
      v-if="!buttonOnly && buttonToggled"
      class="fixed z-[9998] top-0 left-0 w-full h-full table text-subtitle"
      :class="[
        fullScreenOnMobile && 'fullscreen-mobile-filter',
        mobileFiltersOnLargerScreens
          ? 'desktop-styles--container'
          : 'sm:inline-block sm:absolute sm:bg-opacity-0 sm:top-[calc(100%+12px)] sm:w-368 sm:h-auto sm:margin-0 sm:bg-none',
      ]"
      @touchstart="closeModal"
    >
      <div class="inline-block w-full" @mousedown.prevent="closeModal" @touchstart.stop>
        <div
          role="dialog"
          aria-modal="true"
          :aria-label="modalLabel"
          class="bg-foreground box-shadow-static-lg px-16 pb-16 w-full rounded-t-xl bottom-0 fixed overflow-auto max-h-[85vh]"
          :class="mobileFiltersOnLargerScreens ? 'desktop-styles--dialog' : 'sm:static sm:m-auto sm:rounded-xl'"
          @mousedown.stop
        >
          <div class="bg-foreground flex sticky top-0 py-16 z-[1]">
            <h2 class="text-h4 text-grey-header">{{ modalLabel }}</h2>
            <button class="modal-close ml-auto" aria-label="Close" @click="closeModal">
              <CloseIcon color="#4B4C52" aria-hidden="true" />
            </button>
          </div>
          <slot :close-modal="closeModal" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import CaretIcon from '../../graphics/icons/CaretIcon.vue';
import CloseIcon from '../../graphics/icons/CloseIcon.vue';

defineOptions({ name: 'SearchFilterToken' });
const props = withDefaults(
  defineProps<{
    buttonLabel?: string;
    modalLabel?: string;
    selected?: boolean;
    mobileFiltersOnLargerScreens?: boolean;
    fullScreenOnMobile?: boolean;
    buttonClasses?: string;
    buttonOnly?: boolean;
  }>(),
  {
    buttonLabel: '',
    modalLabel: '',
    selected: false,
    mobileFiltersOnLargerScreens: false,
    fullScreenOnMobile: false,
    buttonClasses: '',
    buttonOnly: false,
  }
);
const emit = defineEmits<{ click: []; close: [] }>();
const buttonToggled = ref(false);
const selectedClass = computed(() => (props.selected || (!props.buttonOnly && buttonToggled.value) ? ' selected' : ''));
const iconDirection = computed(() => (buttonToggled.value ? 'rotate-180' : ''));

function buttonHandler(): void {
  if (props.buttonOnly) {
    emit('click');
    return;
  }
  buttonToggled.value = !buttonToggled.value;
}

function closeModal(event?: Event): void {
  event?.preventDefault();
  buttonToggled.value = false;
  emit('close');
  document.documentElement.classList.remove('overflow-hidden', 'overflow-hidden:sm');
}

function handleDocumentClick(event: MouseEvent): void {
  const { target } = event;
  if (!(target instanceof Element)) return;
  const clickedFilterButton = target.closest('.filter-token-button');
  if (clickedFilterButton && clickedFilterButton.id !== `${props.modalLabel}-filter-token-button`) {
    buttonToggled.value = false;
  }
  if (target.closest('.filter-token') !== null) return;
  buttonToggled.value = false;
}

function handleEscape(event: KeyboardEvent): void {
  if (event.key === 'Escape' && buttonToggled.value) closeModal(event);
}

onMounted(() => {
  if (props.buttonOnly) return;
  document.addEventListener('click', handleDocumentClick);
  document.addEventListener('keydown', handleEscape);
});
onBeforeUnmount(() => {
  document.removeEventListener('click', handleDocumentClick);
  document.removeEventListener('keydown', handleEscape);
});
</script>

<style>
@media only screen and (max-width: 869px) {
  html:has(.fullscreen-mobile-filter) {
    overflow: hidden;
  }
}

/* fix for cookie banner + support button overlap with filter token popups */
@media only screen and (max-width: 870px) {
  main:has(#filter-token-component) ~ #onetrust-consent-sdk #ot-sdk-btn-floating,
  main:has(#filter-token-component) ~ div:has(iframe#launcher) iframe#launcher {
    z-index: 1 !important;
  }
}
</style>
<style lang="scss" scoped>
.filter-token {
  .filter-token-button {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    position: relative;
    white-space: nowrap;
    cursor: pointer;
    color: theme('colors.grey.clickable');
    font-weight: theme('fontWeight.medium');
    outline: none;
    transition: all 250ms ease-out;
    padding: 8px;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      border: theme('borderWidth.1') solid theme('colors.grey.clickable');
      border-radius: theme('borderRadius.md');
      height: 100%;
      width: 100%;
      pointer-events: none;
    }

    &:hover,
    &:focus-visible {
      color: theme('colors.brand.contrast');
      transition: all 100ms ease-out;

      &::before {
        border-color: theme('colors.brand.contrast');
      }
    }

    &:focus-visible::after {
      position: absolute;
      top: 0;
      left: 0;
      width: calc(100% + 8px);
      height: calc(100% + 8px);
      border: 2px dotted theme('colors.brand.contrast');
      margin: -4px;
      border-radius: 4px;
      outline: none;
      pointer-events: none;
    }

    &.selected {
      color: theme('colors.primary.DEFAULT');
      background: theme('colors.primary.subdued');
      transition: all 100ms ease-out;

      &::before {
        border: 2px solid theme('colors.primary.DEFAULT');
      }
    }
  }
  .text-field-input {
    speak-as: spell-out;
  }
  .filter-token-modal {
    .modal-wrapper {
      display: inline-block;
      width: 100%;
    }
  }
  .fullscreen-mobile-filter {
    @media only screen and (max-width: 869px) {
      [role='dialog'] {
        top: 0;
        bottom: auto;
        height: 100dvh;
        max-height: 100dvh;
        border-radius: 0;
        overscroll-behavior: contain;
      }
    }
  }
  .desktop-styles {
    &--container {
      background-color: rgba(0, 0, 0, 0.5);
      @media only screen and (min-width: 870px) {
        background: none;
        display: inline-block;
        position: absolute;
        --tw-bg-opacity: 0;
        top: calc(100% + 12px);
        width: 368px;
        height: auto;
        margin: 0;
      }
    }
    &--dialog {
      @media only screen and (min-width: 870px) {
        position: static;
        margin: auto;
        border-radius: 4px;
      }
    }
  }
}
</style>
